import React, { useState } from 'react'
import { Pencil } from 'lib/lucide'

import { ERRORS } from 'consts'
import { useApi } from 'hooks'
import { snackbar } from 'components'

import { getTags } from './api'
import { DefaultTable } from '../components'
import { GetTagsResponse, Tag } from './types'
import { CreateTagDialog, DeleteTagDialog, UpdateTagDialog } from './components'

const removeAccents = (str?: string | null) => {
  if (!str) return ''
  return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
}

const columns = [
  { title: 'ID', field: 'id' },
  {
    title: 'Título',
    field: 'title',
    customFilterAndSearch: (term: string, rowData: Tag) =>
      removeAccents(rowData.title ?? '')
        .toLowerCase()
        .includes(removeAccents(term ?? '').toLowerCase()),
  },
  {
    title: 'Descripción',
    field: 'description',
    customFilterAndSearch: (term: string, rowData: Tag) =>
      removeAccents(rowData.description ?? '')
        .toLowerCase()
        .includes(removeAccents(term ?? '').toLowerCase()),
  },
  {
    title: 'Activo',
    render: (rowData: Tag) => (rowData.active ? 'Sí' : 'No'),
    sorting: false,
  },
]

const getTagsResponseGetter = (responseData: GetTagsResponse) => responseData?.tags ?? []

const Tags: React.FC = () => {
  // Extraemos refetch para volver a consultar la API
  const [{ data: tags, isLoading }, refetchTags] = useApi<GetTagsResponse, Tag[]>(
    getTags,
    getTagsResponseGetter,
    { baseData: [], onError: () => snackbar.show(ERRORS.GENERIC_ERROR_MESSAGE) },
  )

  const [isCreateTagDialogOpen, setIsCreateTagDialogOpen] = useState(false)
  const [isDeleteTagDialogOpen, setIsDeleteTagDialogOpen] = useState(false)
  const [tagToDelete, setTagToDelete] = useState<Tag | null>(null)
  const [isUpdateTagDialogOpen, setIsUpdateTagDialogOpen] = useState(false)
  const [tagToUpdate, setTagToUpdate] = useState<Tag | null>(null)

  const handleCreateTagDone = async () => {
    await refetchTags()
    setIsCreateTagDialogOpen(false)
  }

  const handleDeleteTagDialogClose = () => {
    setIsDeleteTagDialogOpen(false)
    setTagToDelete(null)
  }

  const handleDeleteTagDone = async () => {
    await refetchTags()
    handleDeleteTagDialogClose()
  }

  const handleUpdateTagDialogClose = () => {
    setIsUpdateTagDialogOpen(false)
    setTagToUpdate(null)
  }

  const handleUpdateTagDone = async () => {
    await refetchTags()
    handleUpdateTagDialogClose()
  }

  const actions = [
    {
      icon: 'add',
      tooltip: 'Nueva etiqueta',
      isFreeAction: true,
      onClick: () => setIsCreateTagDialogOpen(true),
    },
    {
      icon: () => <Pencil size={20} />,
      tooltip: 'Editar etiqueta(tag)',
      onClick: (_: React.SyntheticEvent, row: Tag | Tag[]) => {
        setTagToUpdate(row as Tag)
        setIsUpdateTagDialogOpen(true)
      },
    },
    /* {
      icon: 'delete',
      tooltip: `Eliminar`,
      onClick: (_: React.SyntheticEvent, row: Tag | Tag[]) => {
        setTagToDelete(row as Tag)
        setIsDeleteTagDialogOpen(true)
      },
    }, */
  ]

  return (
    <>
      <DefaultTable
        title="Tags"
        columns={columns}
        data={tags}
        isLoading={isLoading}
        actions={actions}
      />
      {isCreateTagDialogOpen && (
        <CreateTagDialog
          handleClose={() => setIsCreateTagDialogOpen(false)}
          onDone={handleCreateTagDone}
        />
      )}
      {isDeleteTagDialogOpen && tagToDelete && (
        <DeleteTagDialog
          handleClose={handleDeleteTagDialogClose}
          tagId={tagToDelete.id}
          tagTitle={tagToDelete.title}
          onDone={handleDeleteTagDone}
        />
      )}
      {isUpdateTagDialogOpen && tagToUpdate && (
        <UpdateTagDialog
          handleClose={handleUpdateTagDialogClose}
          tagToUpdate={tagToUpdate}
          onDone={handleUpdateTagDone}
        />
      )}
    </>
  )
}

export { Tags }
